import React, { useState, useEffect, useCallback } from 'react';
import { DataGridPro, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid-pro';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Auth } from 'aws-amplify';
import { ContentTitle, RightGrid } from '../ui/styled';
import { useDispatch } from 'react-redux';
import BackIcon from '../common/Back/BackIcon';
import PageHeading from '../common/Heading/PageHeading';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import AssessmentIcon from '@mui/icons-material/Assessment';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CommonSearch from '../common/Search/CommonSearch';
import {
  CBP_FORM_UPDATE_URL,
  CBP_LIST_URL,
  COUNTY_LIST,
  PROVIDER_ADM_RATES_URL,
  PROVIDER_REF_CONTACTS_URL,
  paginationSize,
} from '../../constants';
import { renderCellExpand } from '../common/GridCell/GridCellExpand';
import { yesNoText } from '../common/YesNoText/YesNoText';
import { commonQueryParams } from '../../services/QueryParams/QueryParams';
import { useNavigate } from 'react-router-dom';
import { getCbpCaseManagerReferenceData } from '../../redux/cbp/cbpCaseManager/cbpCaseManagerReferenceSlice';
import { getCbpForm, newCBPForm } from '../../redux/cbp/cbpForm/cbpFormSlice';
import { getCbpFormReferenceData } from '../../redux/cbp/cbpForm/cbpReferenceSlice';
import { LeftGrid } from '../Episode/Episode.Styled';
import { getCbpList, getCountyList } from './services/cbpApi';
import { getContactByUser } from '../Contact/services/contactsApi';
import { postStopApi } from '../../services/StopApi/StopApi';
import ProviderAdmRatesDialog from './ProviderAdmRatesDialog';
import ProviderRefContactsDialog from './ProviderRefContactsDialog';

const CBPList = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [searchCBPList, setSearchCBPList] = useState('');
  const [countyList, setCountyList] = useState([]);
  const [visibleCboIds, setVisibleCboIds] = React.useState('');
  const [CBPList, setCBPList] = useState({
    isLoading: false,
    data: [],
    total: 0,
    page: 1,
    pageSize: 2500,
    sortModel: [{ field:'CBPName', sort: 'asc' }],
    filterModel: {linkOperator: 'and', items: [{ columnField: 'inactive', operatorValue: 'is', value: 'false' }]}
  });
  const [reportsMenuAnchorEl, setReportsMenuAnchorEl] = useState(null);
  const reportsMenuOpen = Boolean(reportsMenuAnchorEl);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [refContactsDialogOpen, setRefContactsDialogOpen] = useState(false);
  const [refContactsData, setRefContactsData] = useState(null);
  const [refContactsLoading, setRefContactsLoading] = useState(false);

  useEffect(() => {
    if (user?.signInUserSession.accessToken.jwtToken) {
      fetchData();
    }
    getUser().then((userData) => setUser(userData));
  }, [
    user?.signInUserSession.accessToken.jwtToken,
    CBPList.pageSize,
    CBPList.page,
    CBPList.sortModel,
    CBPList.filterModel,
    searchCBPList,
  ]);

  const getUser = () => {
    return Auth.currentAuthenticatedUser()
      .then(function (user) {
        getContactByUser(user)
          .then(function (loggedInContact) {
            user.contact = loggedInContact;
          })
          .catch(() => console.log('Not signed in'));
        //console.log(user)
        return user;
      })
      .catch(() => console.log('Not signed in'));
  };

  useEffect(() => {
    if (user?.signInUserSession.accessToken.jwtToken) {
      if (countyList.length==0)
        fetchCounties();
    }
  }, [user?.signInUserSession.accessToken.jwtToken]);

  const handleFilterChange = useCallback((filterModel) => {
    // Here you save the data you need from the filter model
    //console.log(filterModel);
    setCBPList((old) => ({
      ...old,
      filterModel: { ...filterModel },
    }));
  }, []);

  const fetchCounties = async () => {
    const commonParams = COUNTY_LIST;
    const authorization = `${user?.signInUserSession.accessToken.jwtToken}`;
    const countyList = await getCountyList({
      commonParams,
      authorization,
    });
    setCountyList(countyList.data);    
  };

  const getCountyName = (countyId) => {
    const getCounty = countyList.find((item) => item.id === countyId);
    return getCounty?.value;
  };

  const QuickSearchToolbar = () => {
    return (
      <Box pt={1} sx={{ flexGrow: 1, backgroundColor: '#747070' }}>
        <Grid container spacing={1}>
          <Grid item xs={11}>
            <CommonSearch
              searchText={searchCBPList}
              setSearchText={setSearchCBPList}
            />
          </Grid>
          <Grid item xs={1}>  
            <GridToolbarContainer>
              <GridToolbarExport />
            </GridToolbarContainer>
          </Grid>
        </Grid>    
      </Box>  
    );
  };

  const fetchData = async () => {
    setCBPList((old) => ({ ...old, isLoading: true }));
    const commonParams = commonQueryParams(
      CBP_LIST_URL,
      searchCBPList,
      {...CBPList, pageSize:'2500'}
    );
    const authorization = `${user?.signInUserSession.accessToken.jwtToken}`;
    dispatch(getCbpFormReferenceData(authorization));
    dispatch(getCbpCaseManagerReferenceData(authorization));
    const cbps = await getCbpList({
      commonParams,
      authorization,
    });
    setCBPList((old) => ({
      ...old,
      ...cbps,
    }));
    let IDs = '';
    cbps.data.forEach(function (item) {
      IDs += (item.CBPID + ','); 
    });
    setVisibleCboIds(IDs.slice(0, -1));
    //console.log(IDs);
  };

  const handleSortModelChange = useCallback((sortModel) => {
    // Here you save the data you need from the sort model
    setCBPList((old) => ({ ...old, sortModel: [...sortModel] }));
  }, []);

  const cbpRowClick = (rowData) => {
    if (user?.contact?.cntDept===4 || user?.contact?.cntDept===1) {
      const commonParams = `${CBP_FORM_UPDATE_URL}/${rowData.CBPID}`;
      const authorization = `${user?.signInUserSession.accessToken.jwtToken}`;
      dispatch(getCbpForm({ commonParams, authorization }));
      navigate(`/cbp/details/${rowData.CBPID}`);
    }
  };

  const handelNewCBPForm = () => {
    const newEmptyCBPForm = {
      newCbpForm: true,
      cboID: '',
      cboName: '',
      organization: '',
      acceptsCCCMS: '',
      cbpCode: '',
      firstReferralContact: '',
      secondReferralContact: '',
      lastVerWk: '',
      cup: '',
      modality: '',
      enhancedServicesRate: '',
      sTOPBeds: '',
      verificationContact: '',
      verificationContact2: '',
      slecupNALtrDate: '',
      cupMaxBeds: '',
      gender: '',
      blanketPurchaseAgreementDedCapSap: '',
      maxAdmits: '',
      wcVeificationContact1: '',
      wcVeificationContact2: '',
      aDPEffDate: '',
      aDPLicCert: '',
      accepts290s: '',
      foodProvided: '',
      calOMSContact: '',
      wcCalOMSContacts1: '',
      wcCalOMSContact2: '',
      aDPExpDate: '',
      ADPMaxBeds: '',
      dataDepartmentComment: '',
      sap: '',
      bPADedCap: '',
      inactive: '',
      flagged: '',
      verLock: '',
      cupNA: '',
      currentFYRateSheetReceivedandVerified: '',
      overorUnderDARSApprovedCountyRate: '',
      contractComplianceStatusNote: '',
      lowIntensity: '',
      medIntensity: '',
      highIntensity: '',
    };
    dispatch(newCBPForm(newEmptyCBPForm));
    navigate(`/cbp/details/0`);
  };

  const handleReportsMenuClick = (event) => {
    setReportsMenuAnchorEl(event.currentTarget);
  };

  const handleReportsMenuClose = () => {
    setReportsMenuAnchorEl(null);
  };

  const handleReportAction = async (reportType) => {
    if (reportType === 'org-adm-cnt-rates') {
      handleReportsMenuClose();
      setReportLoading(true);
      setReportDialogOpen(true);

      try {
        const authorization = user?.signInUserSession.accessToken.jwtToken;
        const payload = {
          cboIds: visibleCboIds
        };

        const response = await postStopApi({
          url: PROVIDER_ADM_RATES_URL,
          payload,
          authorization
        });

        if (response.succeeded === true) {
          setReportData(response);
        } else {
          alert(response.message || 'Failed to generate report');
          setReportDialogOpen(false);
        }
      } catch (error) {
        console.error('Error generating report:', error);
        alert('Error generating report: ' + error);
        setReportDialogOpen(false);
      } finally {
        setReportLoading(false);
      }
    } else if (reportType === 'ref-contacts') {
      handleReportsMenuClose();
      setRefContactsLoading(true);
      setRefContactsDialogOpen(true);

      try {
        const authorization = user?.signInUserSession.accessToken.jwtToken;
        const payload = {
          cboIds: visibleCboIds
        };

        const response = await postStopApi({
          url: PROVIDER_REF_CONTACTS_URL,
          payload,
          authorization
        });

        if (response.succeeded === true) {
          setRefContactsData(response);
        } else {
          alert(response.message || 'Failed to generate report');
          setRefContactsDialogOpen(false);
        }
      } catch (error) {
        console.error('Error generating report:', error);
        alert('Error generating report: ' + error);
        setRefContactsDialogOpen(false);
      } finally {
        setRefContactsLoading(false);
      }
    }
  };

  const columnsData = [
    { field: 'CBPID', headerName: 'CBP Id', flex: 1 },
    {
      field: 'CBPName',
      headerName: 'CBP Name',
      renderCell: renderCellExpand,
      flex: 1,
    },
    {
      field: 'mod',
      headerName: 'Mod',
      flex: 1,
    },
    {
      field: 'gnd',
      headerName: 'Gender',
      flex: 1,
    },
    {
      field: 'SAP',
      headerName: 'SAP',
      flex: 1,
      type: 'boolean',
    },
    {
      field: 'reg290',
      headerName: '290 Reg',
      renderCell: (params) => yesNoText(params),
      flex: 1,
      type: 'boolean',
    },
    {
      field: 'CCCMS',
      headerName: 'CCC MS',
      renderCell: (params) => yesNoText(params),
      flex: 1,
      type: 'boolean',
    },
    // {
    //   field: 'CCCEnhSer290',
    //   headerName: '290 CCC Enh Ser',
    //   renderCell: (params) => yesNoText(params),
    //   minWidth: 80,
    //   flex: 1,
    // },
    {
      field: 'orgCnyId',
      headerName: 'County',
      // renderCell: (params) => {
      //   return (
      //     <Typography sx={{fontSize: '15px;'}}>{getCountyName(params.value)}</Typography>
      //   )
      // },
      valueGetter: (params) => {return getCountyName(params.value)},
      flex: 1,
    },
    {
      field: 'dataDeptComment',
      headerName: 'Date Dept. Comment',
      renderCell: renderCellExpand,
      flex: 1,
      minWidth: 200,
      filterable: false
    },
    {
      field: 'contractComplianceStatusComment',
      headerName: 'Contract Compliance Status Comment',
      renderCell: renderCellExpand,
      flex: 1,
      minWidth: 200,
      filterable: false
    },
    { field: 'inactive', headerName: 'Inactive', flex: 1, type: 'boolean' },
    // {
    //   field: 'dedicatedCapacity',
    //   headerName: 'Decicated Capacity',
    //   flex: 1,
    //   type: 'boolean',
    // },
    // {
    //   field: 'fundingSAP',
    //   headerName: 'Funding SAP',
    //   flex: 1,
    // },
  ];

  return (
    <>
      <ContentTitle container>
        <RightGrid item xs={12} sm={4}>
          <BackIcon />
          <PageHeading title='CBP List' />
        </RightGrid>
        <Grid item xs={12} sm={4}>
          <Box style={{ alignItems: 'center' }}>
            <Typography variant='h4' align='center'>Providers</Typography>
          </Box>
        </Grid>
        <LeftGrid item xs={12} sm={4}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant='outlined'
              startIcon={<AssessmentIcon />}
              endIcon={<KeyboardArrowDownIcon />}
              onClick={handleReportsMenuClick}
              disabled={user?.contact?.cntDept !== 4 && user?.contact?.cntDept !== 1}
            >
              Reports
            </Button>
            <Button
              variant='contained'
              disabled={user?.contact?.cntDept !== 4 && user?.contact?.cntDept !== 1}
              onClick={() => handelNewCBPForm()}
            >
              New Provider
            </Button>
          </Box>
        </LeftGrid>
      </ContentTitle>
      <Menu
        anchorEl={reportsMenuAnchorEl}
        open={reportsMenuOpen}
        onClose={handleReportsMenuClose}
        PaperProps={{
          style: {
            maxHeight: 400,
            width: 280
          }
        }}
      >
        <MenuItem onClick={() => handleReportAction('org-adm-cnt-rates')}>
          Admin Org Contact and Rates
        </MenuItem>
        <MenuItem onClick={() => handleReportAction('ref-contacts')}>
          CBP List with Referral Contacts
        </MenuItem>
      </Menu>
      <ProviderAdmRatesDialog
        open={reportDialogOpen}
        onClose={() => setReportDialogOpen(false)}
        reportData={reportData}
        loading={reportLoading}
      />
      <ProviderRefContactsDialog
        open={refContactsDialogOpen}
        onClose={() => setRefContactsDialogOpen(false)}
        reportData={refContactsData}
        loading={refContactsLoading}
      />
      <div style={{ height: 670, width: '100%' }}>
        <div style={{ display: 'flex', height: '100%' }}>
          <div style={{ flexGrow: 1 }}>
            <DataGridPro
              //autoHeight
              //getRowHeight={() => 'auto'}
              filterMode="server"
              onFilterModelChange={handleFilterChange}
              columns={columnsData}
              rows={CBPList.data}
              pageSize={CBPList.pageSize}
              page={CBPList.page - 1}
              rowCount={CBPList.total}
              paginationMode='server'
              sortingMode='server'
              onPageSizeChange={(newPageSize) =>
                setCBPList((old) => ({ ...old, pageSize: newPageSize }))
              }
              onPageChange={(newPage) => {
                setCBPList((old) => ({ ...old, page: newPage + 1 }));
              }}
              rowsPerPageOptions={paginationSize}
              pagination
              components={{ Toolbar: QuickSearchToolbar }}
              // experimentalFeatures={{ newEditingApi: true }}
              onSortModelChange={handleSortModelChange}
              onRowClick={(param) => cbpRowClick(param.row)}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
              }
              loading={CBPList.isLoading}
            />
          </div>  
        </div>
      </div>    
    </>
  );
};
export default CBPList;
